import { useQuery } from '@tanstack/react-query';
import { fetchJson } from '~/utils/fetch-json-v2';

type FileMetadata = {
  urn: string;
  owner_urn: string;
  key: string;
  name: string;
  size: number;
  type: string;
  url: string;
  is_public: boolean;
  created_at: string;
  updated_at: string;
};

/**
 * Hook to resolve Bloque file DIDs to actual URLs
 * @param logoUrl - The logo URL which might be a DID or regular URL
 * @returns The resolved URL and loading state
 */
export function useFileUrl(logoUrl: string | undefined | null) {
  const isBloqueDid = logoUrl?.startsWith('did:bloque:file:');

  const { data, isLoading, error } = useQuery({
    queryKey: ['file-url', logoUrl],
    queryFn: async (): Promise<string> => {
      if (!logoUrl || !isBloqueDid) {
        return logoUrl || '/logo.png';
      }

      try {
        const response = await fetchJson<FileMetadata>(`/files/${logoUrl}`);
        return response.data?.url || '/logo.png';
      } catch {
        return '/logo.png';
      }
    },
    enabled: !!logoUrl,
    staleTime: 30 * 60 * 1000, // 30 minutes (since URL is valid for 1 hour)
    gcTime: 60 * 60 * 1000, // 1 hour
  });

  return {
    url: data || '/logo.png',
    isLoading: isLoading && isBloqueDid,
    error,
  };
}
